import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

interface WeeklyPeriod {
  day: string;
  start_time: string;
  end_time: string;
}

interface AvailabilityRule {
  availability_rule_id: string;
  tzid: string;
  weekly_periods: WeeklyPeriod[];
}

const dayMap: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};
export const detectTimezone = () => {
  try {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (detectedTimezone) {
      return detectedTimezone;
    }
    return 'UTC';
  } catch (error) {
    console.error('Timezone detection failed:', error);
    return 'UTC';
  }
};

export const convertRuleTimezone = (
  rule: AvailabilityRule | undefined,
  targetTimezone: string,
): AvailabilityRule => {
  if (!rule) {
    return {
      availability_rule_id: '',
      tzid: '',
      weekly_periods: [],
    };
  }
  const sourceTz = rule.tzid;
  const newPeriods: any[] = [];

  rule.weekly_periods.forEach((period: any) => {
    const dayIndex = dayMap[period.day.toLowerCase()];
    const [startH, startM] = period.start_time.split(':').map(Number);
    const [endH, endM] = period.end_time.split(':').map(Number);

    // 1. Define the interval in Source Timezone
    const sourceStart = dayjs()
      .tz(sourceTz)
      .startOf('week')
      .day(dayIndex)
      .hour(startH)
      .minute(startM)
      .second(0);
    let sourceEnd = dayjs()
      .tz(sourceTz)
      .startOf('week')
      .day(dayIndex)
      .hour(endH)
      .minute(endM)
      .second(0);

    // Handle overnight shifts in the SOURCE data (e.g., 22:00 - 02:00)
    if (sourceEnd.isBefore(sourceStart)) {
      sourceEnd = sourceEnd.add(1, 'day');
    }

    // 2. Convert to Target Timezone
    const targetStart = sourceStart.tz(targetTimezone);
    const targetEnd = sourceEnd.tz(targetTimezone);

    // 3. Check if the shift now spans across a midnight in the Target Timezone
    if (targetStart.format('YYYY-MM-DD') !== targetEnd.format('YYYY-MM-DD')) {
      // Split into TWO periods

      // Period A: From start time until 23:59 of that day
      newPeriods.push({
        day: targetStart.format('dddd').toLowerCase(),
        start_time: targetStart.format('HH:mm'),
        end_time: '23:59',
      });

      // Period B: From 00:00 of the NEXT day until the end time
      newPeriods.push({
        day: targetEnd.format('dddd').toLowerCase(),
        start_time: '00:00',
        end_time: targetEnd.format('HH:mm'),
      });
    } else {
      // It stays within the same day
      newPeriods.push({
        day: targetStart.format('dddd').toLowerCase(),
        start_time: targetStart.format('HH:mm'),
        end_time: targetEnd.format('HH:mm'),
      });
    }
  });

  // Optional: Sort periods by day and time for cleanliness
  const sortedPeriods = newPeriods.sort(
    (a, b) =>
      dayMap[a.day] - dayMap[b.day] || a.start_time.localeCompare(b.start_time),
  );

  return {
    ...rule,
    tzid: targetTimezone,
    weekly_periods: sortedPeriods,
  };
};
